// File: frontend/src/content/legalMarkdown.js
//
// Shared Markdown renderer for PesaFii's public legal documents.
//
// PesaFii publishes its legal documents from a single source of truth per
// document (privacyPolicy.js, termsOfService.js). Each one is rendered twice:
// as a React page under /privacy or /terms, and as a committed markdown file
// under docs/legal/. A Jest sync test byte-compares the committed markdown
// against this renderer's output, so the published page and the legal record
// can never drift apart.
//
// This module exists so the two documents share ONE definition of that
// markdown shape. When the Terms were added the alternative was a second
// hand-written renderer, which would have let the two documents' heading
// levels, spacing and front-matter diverge silently over time.
//
// CommonJS on purpose: webpack/CRA imports it fine from ES modules, and the
// generator scripts require() it under plain Node without transpiling.

/**
 * Render a legal document as Markdown.
 *
 * Deterministic: same inputs, same bytes. The generator scripts write the
 * result straight to docs/legal/, and the *Sync tests re-run it and compare.
 *
 * @param {object}   doc
 * @param {string}   doc.title          H1 of the document
 * @param {string[]} doc.banner         blockquote lines placed under the H1
 * @param {string}   doc.version
 * @param {string}   doc.effectiveDate
 * @param {string}   doc.lastUpdated
 * @param {Array<{id:string,title:string,blocks:Array}>} doc.sections
 *        Block shapes, matching what the React pages render:
 *          { p: "paragraph" } | { h3: "sub-heading" } | { ul: ["item", ...] }
 * @returns {string} markdown, newline-terminated
 */
function renderLegalMarkdown({ title, banner, version, effectiveDate, lastUpdated, sections }) {
  const lines = [];
  lines.push(`# ${title}`);
  lines.push("");
  lines.push(...banner);
  lines.push("");
  lines.push(`**Version:** ${version}`);
  lines.push(`**Effective date:** ${effectiveDate}`);
  lines.push(`**Last updated:** ${lastUpdated}`);
  for (const [i, s] of sections.entries()) {
    lines.push("");
    lines.push(`## ${i + 1}. ${s.title}`);
    for (const b of s.blocks) {
      lines.push("");
      if (b.h3) lines.push(`### ${b.h3}`);
      else if (b.p) lines.push(b.p);
      else if (b.ul) for (const item of b.ul) lines.push(`- ${item}`);
    }
  }
  lines.push("");
  return lines.join("\n");
}

module.exports = { renderLegalMarkdown };
